import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/lib/firebase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/authStore';
import { Button } from '@/components/ui/button';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { toast } from '@/lib/toast';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [resending, setResending] = useState(false);

  // Poll Firebase for email verification status (sequential to prevent overlap)
  useEffect(() => {
    let active = true;

    const poll = async () => {
      while (active) {
        await new Promise((r) => setTimeout(r, 5000));
        if (!active) break;

        const currentUser = auth.currentUser;
        if (!currentUser) continue;

        try {
          await currentUser.reload();
          if (currentUser.emailVerified) {
            useAuthStore.getState().setUser(currentUser);
            toast.success('Email verified!');
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch {
          // Ignore reload errors, will retry on next iteration
        }
      }
    };

    poll();
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleResend = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setResending(true);
    try {
      await apiClient.sendVerificationEmail();
      toast.success('Verification email sent!');
    } catch {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  }, []);

  // If already verified (e.g., navigated here directly), redirect
  if (user?.emailVerified) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="text-5xl">📧</div>
        <h1 className="text-2xl font-bold text-foreground">Verify Your Email</h1>
        <p className="text-muted-foreground">
          We sent a verification link to{' '}
          <span className="font-medium text-foreground">{user?.email}</span>. Click the link in your
          email to continue.
        </p>

        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <AnkhSpinner size="lg" />
          <span>Waiting for verification...</span>
        </div>

        <div className="space-y-3 pt-4">
          <Button variant="outline" className="w-full" onClick={handleResend} disabled={resending}>
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Check your spam folder if you don't see it.
          </p>
        </div>
      </div>
    </div>
  );
}
